import { IcEdit, IcTrash } from '@assets/icons/24x24';
import { Button, ButtonGroup, Icon, IconButton, Text } from '@chakra-ui/react';
import { useFlightPlansManagementContext } from '@components/cockpit/panels/flightPlan/FlightPlansManagementContext';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveUserLogin } from '@redux/authent/authent.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { usePatchStatusMutation } from '@services/utm/activation.api';
import { isBefore } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import { ActivationAndFlightPlan, ActivationStatusEnum } from '@/types/utm/flightPlan.types';

type Props = {
  activationAndFlightPlan: ActivationAndFlightPlan;
  showEditAndDeleteButtons?: boolean;
};

export default function ActivationButtons({
  activationAndFlightPlan,
  showEditAndDeleteButtons = true,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const {
    canEditFlightPlan,
    canDeleteFlightPlan,
    canValidateFlightPlan,
    canCreateFlightPlan,
    setEditedFlightPlanCode,
    setSelectedForDeletionFlightPlan,
    dispatch,
  } = useFlightPlansManagementContext();
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const situationTime = useAppSelector(selectSituationTime);
  const [patchStatus, { isLoading }] = usePatchStatusMutation();

  const { flightPlan, activation } = activationAndFlightPlan;

  const handleUpdateStatus = (status: ActivationStatusEnum) => {
    patchStatus({ id: activation.id, status })
      .unwrap()
      .then((activationAndFlightPlan) => {
        dispatch({
          type: 'UPDATE',
          payload: { activationId: activation.id, activationAndFlightPlan },
        });
        createToast(
          formatMessage({ id: 'components.flightPlans.message.updateStatus' }, { name: flightPlan.name }),
          ToastStatusEnum.SUCCESS,
        );
      });
  };

  const getActionButtonByActivationStatus = () => {
    switch (activation.status) {
      case ActivationStatusEnum.DRAFT:
        return (
          (canEditFlightPlan(activationAndFlightPlan) ||
            (canCreateFlightPlan && flightPlan.createdBy === activeUserLogin)) && (
            <Button
              loading={isLoading}
              variant="toggle"
              width="209px"
              size="toggleM"
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateStatus(ActivationStatusEnum.SUBMITTED);
              }}
            >
              <Text>
                <FormattedMessage id="components.flightPlans.submit" />
              </Text>
            </Button>
          )
        );
      case ActivationStatusEnum.ACCEPTED:
        return (
          canValidateFlightPlan && (
            <Button
              loading={isLoading}
              variant="toggle"
              width="209px"
              size="toggleM"
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateStatus(ActivationStatusEnum.SUBMITTED);
              }}
            >
              <Text>
                <FormattedMessage id="components.flightPlans.cancelAccept" />
              </Text>
            </Button>
          )
        );
      case ActivationStatusEnum.REJECTED:
        return (
          canValidateFlightPlan && (
            <Button
              loading={isLoading}
              variant="toggle"
              width="209px"
              size="toggleM"
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateStatus(ActivationStatusEnum.SUBMITTED);
              }}
            >
              <Text>
                <FormattedMessage id="components.flightPlans.cancelReject" />
              </Text>
            </Button>
          )
        );
      case ActivationStatusEnum.SUBMITTED:
        return (
          canValidateFlightPlan && (
            <ButtonGroup gap={0.5} height="100%" width="209px">
              <Button
                loading={isLoading}
                variant="toggle"
                flex={1}
                height="48px"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(ActivationStatusEnum.ACCEPTED);
                }}
              >
                <Text>
                  <FormattedMessage id="global.validate" />
                </Text>
              </Button>
              <Button
                loading={isLoading}
                variant="toggle"
                flex={1}
                height="48px"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(ActivationStatusEnum.REJECTED);
                }}
              >
                <Text>
                  <FormattedMessage id="global.reject" />
                </Text>
              </Button>
            </ButtonGroup>
          )
        );
      default:
        return null;
    }
  };

  const canSeeEditButton = canEditFlightPlan(activationAndFlightPlan);
  const canSeeDeleteButton = canDeleteFlightPlan(activationAndFlightPlan);

  if (
    (!canSeeEditButton && !canSeeDeleteButton && !canValidateFlightPlan && !canCreateFlightPlan) ||
    flightPlan.externalId !== null ||
    isBefore(activation.startDate, situationTime ?? new Date())
  ) {
    return null;
  }

  return (
    <ButtonGroup attached height="100%" flexShrink={0}>
      {getActionButtonByActivationStatus()}
      {canSeeEditButton && showEditAndDeleteButtons && (
        <IconButton
          variant="transparent"
          boxSize="56px"
          aria-label="edit"
          onClick={(e) => {
            e.stopPropagation();
            setEditedFlightPlanCode(flightPlan.code);
          }}
        >
          <Icon as={IcEdit} boxSize="24px" />
        </IconButton>
      )}
      {canSeeDeleteButton && showEditAndDeleteButtons && (
        <IconButton
          variant="transparent"
          boxSize="56px"
          aria-label="delete"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedForDeletionFlightPlan({
              name: flightPlan.name,
              id: activation.id,
            });
          }}
        >
          <Icon as={IcTrash} boxSize="24px" />
        </IconButton>
      )}
    </ButtonGroup>
  );
}
